function kitFormatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = Number(cents) || 0;
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(number) || number == null) return '0';
    const numString = (number / 100).toFixed(precision);
    const parts = numString.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  let result = '';
  switch (formatString.match(placeholderRegex)?.[1]) {
    case 'amount':
      result = formatWithDelimiters(value, 2);
      break;
    case 'amount_no_decimals':
      result = formatWithDelimiters(value, 0);
      break;
    case 'amount_with_comma_separator':
      result = formatWithDelimiters(value, 2, '.', ',');
      break;
    case 'amount_with_space_separator':
      result = formatWithDelimiters(value, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      result = formatWithDelimiters(value, 0, '.', ',');
      break;
    default:
      result = formatWithDelimiters(value, 2);
  }

  return formatString.replace(placeholderRegex, result);
}

class KitItemPicker extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  connectedCallback() {
    this.variantData = this.getVariantData();
    this.sizeOptionPosition = Number(this.dataset.sizeOptionPosition) || 1;
    this.isSizeOnly = this.dataset.sizeOnly === 'true';
    this.updateVariantStatuses();
    this.updateSelection();
    this.dispatchKitChange();
  }

  onVariantChange() {
    this.updateVariantStatuses();
    this.updateSelection();
    this.dispatchKitChange();
  }

  getSelectedSize() {
    const checked = this.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  getCurrentVariant() {
    const defaultInput = this.querySelector('[data-kit-default-variant]');
    if (defaultInput) {
      const variantId = Number(defaultInput.value);
      const fromData = this.variantData.find((v) => v.id === variantId);
      return fromData || { id: variantId, title: 'Padrão', available: true, options: [], price: 0 };
    }

    const sizeValue = this.getSelectedSize();
    if (!sizeValue) return this.getDefaultVariant();

    if (this.isSizeOnly) {
      const optionIndex = this.sizeOptionPosition - 1;
      return (
        this.variantData.find((variant) => variant.available && variant.options[optionIndex] === sizeValue) ||
        this.variantData.find((variant) => variant.options[optionIndex] === sizeValue)
      );
    }

    const fieldsets = [...this.querySelectorAll('fieldset')];
    const options = fieldsets.map((fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value || null);
    if (options.includes(null)) return this.getDefaultVariant();

    return this.variantData.find((variant) => {
      return !variant.options.map((option, index) => options[index] === option).includes(false);
    });
  }

  getDefaultVariant() {
    return (
      this.variantData.find((variant) => variant.available) ||
      this.variantData[0] ||
      null
    );
  }

  getSelectedSizeLabel() {
    return this.getSelectedSize() || null;
  }

  updateVariantStatuses() {
    const inputs = [...this.querySelectorAll('input[type="radio"]')];
    if (!inputs.length) return;

    const optionIndex = this.sizeOptionPosition - 1;

    inputs.forEach((input) => {
      const hasAvailable = this.variantData.some(
        (variant) => variant.available && variant.options[optionIndex] === input.value
      );

      if (hasAvailable) {
        input.classList.remove('disabled');
        input.removeAttribute('disabled');
      } else {
        input.classList.add('disabled');
        input.setAttribute('disabled', 'disabled');
        if (input.checked) input.checked = false;
      }
    });
  }

  updateSelection() {
    const variant = this.getCurrentVariant();
    const sizeLabel = this.getSelectedSizeLabel();
    const card = this.closest('.kit-item-card');
    const summary = card?.querySelector('[data-kit-selection-summary]');
    const summaryValue = card?.querySelector('[data-kit-selection-value]');
    const priceEl = card?.querySelector('[data-kit-item-price]');
    const image = card?.querySelector('[data-kit-item-image]');
    const hasCustomImage = image?.dataset.customImage === 'true';

    if (priceEl && variant?.price != null) {
      const moneyFormat = this.closest('kit-product-picker')?.dataset.moneyFormat;
      priceEl.textContent = kitFormatMoney(variant.price, moneyFormat);
    }

    if (!summary || !summaryValue) return;

    if (sizeLabel) {
      summary.hidden = false;
      summaryValue.textContent = `Tamanho ${sizeLabel}`;
      if (!hasCustomImage && variant?.featured_image?.src && image) {
        image.src = variant.featured_image.src;
      }
    } else {
      summary.hidden = true;
      summaryValue.textContent = '';
    }
  }

  dispatchKitChange() {
    this.dispatchEvent(
      new CustomEvent('kit-item:change', {
        bubbles: true,
        detail: {
          itemIndex: Number(this.dataset.itemIndex),
          productId: this.dataset.productId,
          productTitle: this.dataset.productTitle,
          size: this.getSelectedSizeLabel(),
          variant: this.getCurrentVariant(),
        },
      })
    );
  }

  getVariantData() {
    const script = this.querySelector('[type="application/json"]');
    if (!script) return [];
    return JSON.parse(script.textContent);
  }

  getSelectedVariant() {
    return this.getCurrentVariant();
  }
}

customElements.define('kit-item-picker', KitItemPicker);

class KitProductPicker extends HTMLElement {
  constructor() {
    super();
    this.selections = {};
    this.onKitItemChange = this.onKitItemChange.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.formId = this.dataset.formId;
    this.kitTitle = this.dataset.kitTitle;
    this.moneyFormat = this.dataset.moneyFormat;

    this.addEventListener('kit-item:change', this.onKitItemChange);
    this.initInfoModal();
    this.initSummaryDropdown();

    this.itemPickers = [...this.querySelectorAll('kit-item-picker')];
    this.itemPickers.forEach((picker) => {
      const variant = picker.getSelectedVariant();
      const index = String(picker.dataset.itemIndex);
      if (variant) {
        this.selections[index] = {
          productTitle: picker.dataset.productTitle,
          size: picker.getSelectedSizeLabel(),
          variant,
        };
      }
    });

    this.updateSummary();
    this.hookProductForm();
  }

  disconnectedCallback() {
    this.removeEventListener('kit-item:change', this.onKitItemChange);
    window.removeEventListener('resize', this.syncSummaryDropdown);
    if (this.productForm) {
      this.productForm.removeEventListener('submit', this.onFormSubmit, true);
    }
  }

  hookProductForm() {
    this.productForm = document.getElementById(this.formId);
    if (!this.productForm) return;
    this.productForm.addEventListener('submit', this.onFormSubmit, true);
  }

  initInfoModal() {
    this.infoModal = this.querySelector('[data-kit-info-modal]');
    this.infoModalTitle = this.querySelector('[data-kit-info-modal-title]');
    this.infoModalBody = this.querySelector('[data-kit-info-modal-body]');
    this.onInfoModalKeydown = this.onInfoModalKeydown.bind(this);

    this.querySelectorAll('[data-kit-info-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.openInfoModal(trigger));
    });

    this.querySelectorAll('[data-kit-info-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeInfoModal());
    });
  }

  initSummaryDropdown() {
    this.summaryDetails = this.querySelector('[data-kit-summary]');
    this.syncSummaryDropdown = this.syncSummaryDropdown.bind(this);
    this.syncSummaryDropdown();
    window.addEventListener('resize', this.syncSummaryDropdown);
  }

  syncSummaryDropdown() {
    if (!this.summaryDetails) return;
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    this.summaryDetails.open = isDesktop;
  }

  openInfoModal(trigger) {
    const targetId = trigger.dataset.infoTarget;
    const source = document.getElementById(targetId);
    if (!this.infoModal || !source || !this.infoModalTitle || !this.infoModalBody) return;

    this.infoModalTitle.textContent = trigger.dataset.infoTitle || 'Informações';
    this.infoModalBody.innerHTML = source.innerHTML;
    this.infoModal.hidden = false;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onInfoModalKeydown);

    const firstAccordion = this.infoModalBody.querySelector('.ars-accordion');
    if (firstAccordion) firstAccordion.open = true;
  }

  closeInfoModal() {
    if (!this.infoModal) return;
    this.infoModal.hidden = true;
    if (this.infoModalBody) this.infoModalBody.innerHTML = '';
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.onInfoModalKeydown);
  }

  onInfoModalKeydown(event) {
    if (event.key === 'Escape') this.closeInfoModal();
  }

  onKitItemChange(event) {
    const { itemIndex, productTitle, size, variant } = event.detail;
    this.selections[String(itemIndex)] = { productTitle, size, variant };
    this.updateSummary();
    this.clearError();
  }

  pickerRequiresSize(picker) {
    return Boolean(picker.querySelector('input[type="radio"]'));
  }

  getSortedSelections() {
    return Object.entries(this.selections)
      .filter(([, data]) => data?.variant?.id)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([index, data]) => ({ index, ...data }));
  }

  getQuantity() {
    const raw = new FormData(this.productForm).get('quantity');
    const quantity = Number(raw);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  buildCartGroupId() {
    return `kit-${Date.now()}`;
  }

  buildCartItems() {
    const quantity = this.getQuantity();
    const groupId = this.buildCartGroupId();
    const combinationParts = [];

    const pieces = this.getSortedSelections().map((data) => {
      const label = this.getItemLabel(data.index);
      const sizeText = data.size ? `Tam. ${data.size}` : '';
      const pieceSummary = sizeText
        ? `${data.productTitle} — ${sizeText}`
        : data.productTitle;

      combinationParts.push(
        sizeText ? `${data.productTitle} (${data.size})` : data.productTitle
      );

      return { label, pieceSummary, variantId: data.variant.id };
    });

    const combination = combinationParts.join(' + ');

    return pieces.map((piece, index) => {
      const properties = {
        Kit: this.kitTitle,
        Combinação: combination,
        Peça: piece.pieceSummary,
        _kit_group: groupId,
        _kit_component: '1',
        _kit_line: String(index + 1),
      };

      if (index === 0) {
        if (this.dataset.kitImage) properties._kit_image = this.dataset.kitImage;
        if (this.dataset.kitUrl) properties._kit_url = this.dataset.kitUrl;
      }

      return {
        id: piece.variantId,
        quantity,
        properties,
      };
    });
  }

  getItemLabel(index) {
    const card = this.querySelector(`[data-kit-item="${index}"]`);
    return card?.dataset.itemLabel || `Peça ${index}`;
  }

  getTotalPrice() {
    return Object.values(this.selections).reduce((sum, selection) => {
      return sum + (selection?.variant?.price || 0);
    }, 0);
  }

  updatePagePrice(totalCents) {
    const pagePrice = document.getElementById(`price-${this.sectionId}`);
    if (!pagePrice) return;

    const priceItem = pagePrice.querySelector('.price-item--regular');
    if (priceItem) {
      priceItem.textContent = kitFormatMoney(totalCents, this.moneyFormat);
    }
  }

  updateSummary() {
    const summary = this.querySelector('[data-kit-summary]');
    const list = this.querySelector('[data-kit-summary-list]');
    const totalEl = this.querySelector('[data-kit-total-price]');
    const toggleTotalEl = this.querySelector('[data-kit-summary-toggle-total]');
    if (!summary || !list || !totalEl) return;

    const items = Object.entries(this.selections)
      .map(([index, data]) => {
        if (!data?.variant) return null;
        return { index: Number(index), label: this.getItemLabel(index), ...data };
      })
      .filter(Boolean)
      .sort((a, b) => a.index - b.index);

    if (!items.length) {
      list.innerHTML = '';
      totalEl.textContent = kitFormatMoney(0, this.moneyFormat);
      if (toggleTotalEl) toggleTotalEl.textContent = '';
      return;
    }

    list.innerHTML = items
      .map((data) => {
        const sizeText = data.size ? `Tam. ${data.size}` : '—';
        const priceText = kitFormatMoney(data.variant.price, this.moneyFormat);
        return `<li><strong>${data.label}</strong><span>${data.productTitle}</span><span class="kit-product-picker__summary-size">${sizeText}</span><span class="kit-product-picker__summary-price">${priceText}</span></li>`;
      })
      .join('');

    const total = this.getTotalPrice();
    const formattedTotal = kitFormatMoney(total, this.moneyFormat);
    totalEl.textContent = formattedTotal;
    if (toggleTotalEl) toggleTotalEl.textContent = formattedTotal;
    this.updatePagePrice(total);
  }

  validateSelections() {
    if (!this.itemPickers.length) {
      return { valid: false, message: 'Adicione peças ao kit no editor do tema.' };
    }

    for (const picker of this.itemPickers) {
      const index = String(picker.dataset.itemIndex);
      const selection = this.selections[index];
      const label = this.getItemLabel(index);
      const requiresSize = this.pickerRequiresSize(picker);

      if (!selection?.variant?.id) {
        return { valid: false, message: `Selecione o tamanho da ${label}.` };
      }
      if (requiresSize && !selection.size) {
        return { valid: false, message: `Selecione o tamanho da ${label}.` };
      }
      if (!selection.variant.available) {
        return { valid: false, message: `${selection.productTitle} está indisponível no tamanho selecionado.` };
      }
    }

    return { valid: true };
  }

  getCartElement() {
    if (document.querySelector('cart-drawer')) {
      return document.querySelector('cart-drawer');
    }

    return document.querySelector('cart-notification');
  }

  async fetchDrawerSections(cart) {
    const sectionIds = cart.getSectionsToRender().map((section) => section.id).join(',');
    const response = await fetch(`${routes.cart_url}?sections=${sectionIds}`);

    if (!response.ok) {
      throw new Error('Não foi possível atualizar o carrinho.');
    }

    return response.json();
  }

  hasRenderableSections(cart, sections) {
    if (!sections) return false;

    return cart.getSectionsToRender().every((section) => sections[section.id]);
  }

  async refreshCartDrawer(cart, addResponse = {}) {
    let sections = addResponse.sections;

    if (!this.hasRenderableSections(cart, sections)) {
      sections = await this.fetchDrawerSections(cart);
    }

    if (!this.hasRenderableSections(cart, sections)) {
      throw new Error('Não foi possível atualizar o carrinho deslizante.');
    }

    if (cart.classList.contains('is-empty')) {
      cart.classList.remove('is-empty');
    }

    cart.renderContents({
      ...addResponse,
      sections,
      id: addResponse.id || addResponse.items?.[0]?.id,
      key: addResponse.key || addResponse.items?.[0]?.key,
    });
  }

  async addKitToCart() {
    const items = this.buildCartItems();
    if (!items.length) {
      throw new Error('Nenhuma peça válida para adicionar ao carrinho.');
    }

    const cart = this.getCartElement();
    const payload = { items };

    if (cart) {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = '/cart';
      cart.setActiveElement(document.activeElement);
    }

    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (data.status) {
      throw new Error(data.description || data.message || 'Não foi possível adicionar o kit ao carrinho.');
    }

    return { data, cart };
  }

  onFormSubmit(event) {
    const validation = this.validateSelections();
    if (!validation.valid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError(validation.message);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.clearError();

    const productFormElement = this.productForm?.closest('product-form');
    const submitButton = productFormElement?.querySelector('[type="submit"]');
    const spinner = productFormElement?.querySelector('.loading-overlay__spinner');

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
    }
    if (spinner) spinner.classList.remove('hidden');

    this.addKitToCart()
      .then(async ({ data, cart }) => {
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        await this.refreshCartDrawer(cart, data);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: String(this.buildCartItems()[0]?.id || ''),
          cartData: data,
        });
      })
      .catch((error) => {
        const message = error?.message || 'Não foi possível adicionar o kit ao carrinho.';
        if (productFormElement) {
          productFormElement.handleErrorMessage(message);
        }
        this.showError(message);
      })
      .finally(() => {
        if (submitButton) {
          submitButton.removeAttribute('aria-disabled');
          submitButton.classList.remove('loading');
        }
        if (spinner) spinner.classList.add('hidden');
      });
  }

  showError(message) {
    const errorEl = this.querySelector('[data-kit-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  clearError() {
    const errorEl = this.querySelector('[data-kit-error]');
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }
}

customElements.define('kit-product-picker', KitProductPicker);
